<template>
    <div class="row condition-row">
        <template v-if="!isLoading">
            <div class="col-3">
                <v-select
                    class="form-control"

                    v-model="fieldModel"
                    :options="selectableFieldTypes"
                    :disabled="!canWrite"
                    :clearable="false"

                    searchable
                    filterable
                />

                <required-error-message v-if="$v.field.$dirty && $v.field.$invalid" />
            </div>
            
            <div class="col-3">
                <v-select
                    class="form-control"
                    
                    v-model="operatorModel"
                    :options="selectableOperators"
                    :disabled="!canWrite"
                    :clearable="false"
                />
            </div>

            <div class="col-4">
                <v-select
                    v-if="isDropDown || isMultiDropDown"
                    
                    class="form-control"

                    v-model="valueModel"
                    :options="selectableFieldValues"
                    :disabled="!fieldModel || !canWrite"
                    :clearable="false"

                    :multiple="isMultiDropDown"
                    :closeOnSelect="!isMultiDropDown"

                    searchable
                    filterable
                />

                <input v-else
                       type="text"
                       class="form-control"
                       v-model="valueModel"
                       :disabled="!fieldModel || !canWrite"
                />
                
                <template
                    v-if="$v.value.$dirty && $v.value.$invalid"
                >
                    <required-error-message v-if="!$v.value.required" />             
                    
                    <error-message v-for="message in valueErrorMessages" :message="message" />
                </template>
            </div>

            <div class="col-2">
                <button v-if="canWrite"
                        class="btn btn-outline-danger"
                        @click="$emit('deleteCondition')"
                >
                    <i class="far fa-trash-alt"></i>
                </button>
            </div>
        </template>
        <template v-else>
            <div class="col-12">
                <loader />
            </div>
        </template>
    </div>
</template>

<script>
import api from "@modules/JourneyBuilder/api";
import {errorGrowl} from '@core/Util/growler';
import moment from 'moment';
import sprintf from 'sprintf';
import { checkpointOperators, resolveOperatorByName } from "@modules/JourneyBuilder/Bricks/Checkpoints/checkpoint-operator-definitions";

import resourceMixin from "@core/Util/Mixins/resources";
import {validationMixin} from 'vuelidate';
import { required, numeric } from 'vuelidate/lib/validators';

import VSelect from 'vue-select';
import RequiredErrorMessage from "@core/Components/Errors/RequiredErrorMessage";
import ErrorMessage from "@core/Components/Errors/ErrorMessage";
import Loader from '@modules/JourneyBuilder/Components/Elements/Loader';


export default {
    name: "BrickFilterCondition",
    
    components: {
        VSelect,
        RequiredErrorMessage,
        ErrorMessage,
        Loader
    },
    
    mixins: [resourceMixin, validationMixin],

    validations: {
        field: {
            required
        },
        
        value: {
            required,

            numberValid: (value, ctx) => ctx.valueType !== 'integer' || numeric(value, ctx),
            dateValid: (value, ctx) => ctx.valueType !== 'date' || moment(value, 'DD-MM-YYYY', true).isValid()
        }
    },

    props: {
        field: {
            type: String,
            required: false //Allow null
        },
        
        operator: {
            type: String,
            required: false,
        },
        
        value: {
            type: [String, Array],
            required: false //Allow null
        },
        
        canWrite: {
            type: Boolean,
            required: false,
            default: false
        },
        
        // provides a way to filter (only display these) certain categories
        includedCategoryFilters: {
            type: Array,
            default: () => []
        },

        // provides a way to filter (not display these) certain categories
        excludedCategoryFilters: {
            type: Array,
            default: () => []
        }
    },
    
    computed: {
        selectableFieldTypes() {
            return Object.keys(this.fieldDefinitions)
                .filter(typeName => (this.includedCategoryFilters.length === 0 || this.includedCategoryFilters.includes(typeName)) &&
                                    (this.excludedCategoryFilters.length === 0 || !this.excludedCategoryFilters.includes(typeName))
                );
        },
        
        selectableOperators() {
            return checkpointOperators.map(o => ({
                id: o.name,
                label: o.label
            }));
        },
        
        selectableFieldValues() {
            let options = this.fieldDefinitions[this.field] && this.fieldDefinitions[this.field].options || [];
            
            //Only display not-yet selected values for multidropdowns
            if(this.isMultiDropDown && Array.isArray(this.value)) {
                options = options.filter(option => !this.value.includes(option.id));
            }
            
            return options;
        },
        
        isMultiDropDown() {
            return this.fieldDefinitions[this.field] && this.fieldDefinitions[this.field].isMultiDropDown || false;
        },
        
        isDropDown() {
            return this.fieldDefinitions[this.field] && this.fieldDefinitions[this.field].isDropDown || false;
        },
        
        valueType() {
            return this.fieldDefinitions[this.field] && this.fieldDefinitions[this.field].valueType || 'string';
        },
        
        fieldModel: {
            get() {
                return this.field;
            },
            set(value) {
                this.$v.field.$touch();
                
                this.$emit('update:field', value);
            }
        },
        
        operatorModel: {
            get() {
                return resolveOperatorByName(this.operator);
            },
            set(value) {
                this.$emit('update:operator', value.id);
            }
        },
        
        valueModel: {
            get() {
                if(!this.fieldDefinitions[this.field]) {
                    return null;
                }

                if(!this.isDropDown && !this.isMultiDropDown) {
                    return this.value;
                }
                
                const { options } = this.fieldDefinitions[this.field];
                
                //Dropdowns simply select the first matching value
                if(this.isDropDown) {
                    return options.find(option => option.id === this.value);
                }
                
                //Don't crash when no value has been configured yet
                if(!this.value || !Array.isArray(this.value)) {
                    return [];
                }
                
                return options.filter(option => this.value.includes(option.id));
            },
            set(value) {
                this.$v.value.$touch();
                
                //Account for multi values
                if(this.isMultiDropDown) {
                    value = (value || []).map(v => v.id);
                } else if(this.isDropDown) {
                    //Don't crash when value is cleared
                    value = value && value.id;
                }
                
                this.$emit('update:value', value);
            }
        },

        isValid() {
            return !this.$v.$invalid;
        },
        
        valueErrorMessages() {
            const errors = [];
            
            if(!this.$v.value.numberValid) errors.push(sprintf(this.resource('JourneyBuilder_CustomFields_InvalidNumber_ErrorMessage'), this.value));
            if(!this.$v.value.dateValid) errors.push(sprintf(this.resource('JourneyBuilder_CustomFields_InvalidDate_ErrorMessage'), this.value));
            
            return errors;
        }
    },
    
    data() {
        return {
            /**
             * @type {Object.<String, { name: String, isMultiDropDown: Boolean, isDropDown: Boolean, valueType: String, defaultValue: Object, options: { value: String, label: String }[] }>}
             */
            fieldDefinitions: {},
            
            isLoading: true
        }
    },

    watch: {
        fieldModel(newValue, oldValue) {
            const newFieldDef = this.fieldDefinitions[newValue];
            const oldFieldDef = this.fieldDefinitions[oldValue];
            
            //Upon switching value type -> reset our value
            if(
                !newFieldDef || 
                !oldFieldDef ||
                newFieldDef.isDropDown !== oldFieldDef.isDropDown ||
                newFieldDef.isMultiDropDown !== oldFieldDef.isMultiDropDown
            ) {
                this.valueModel = undefined;
            }
            
            //Only overwrite our current value when a default has been set
            if(newFieldDef && newFieldDef.defaultValue !== undefined) {
                this.valueModel = newFieldDef.defaultValue;
            }
        }
    },
    
    async created() {
        try {
            const remoteFieldDefinitions = await api.getAudienceFilterFieldTypes();
            
            const toOption = remoteFieldDefinition => ({
                id: remoteFieldDefinition.id,
                label: remoteFieldDefinition.value
            });
            
            const getDefaultValue = remoteFieldDefinition => {
                if(!remoteFieldDefinition.isDefault) {
                    return undefined;
                }
                
                const option = toOption(remoteFieldDefinition);
                
                //Each value type has it's own format
                if(remoteFieldDefinition.isMultiDropDown) {
                    return [option];
                } else if (remoteFieldDefinition.isDropDown) {
                    return option;
                }
                
                return option.label;
            }
            
            remoteFieldDefinitions.forEach((remoteFieldDefinition, index) => {
                if(!this.fieldDefinitions[remoteFieldDefinition.type]) {
                    //Set as a reactive property
                    this.$set(
                        this.fieldDefinitions, 
                        remoteFieldDefinition.type, 
                        {
                            defaultValue: getDefaultValue(remoteFieldDefinition),
                            isMultiDropDown: remoteFieldDefinition.isMultiDropDown,
                            isDropDown: remoteFieldDefinition.isDropDown, 
                            options: [],
                            valueType: remoteFieldDefinition.valueType
                        }
                    );
                }

                //Map to VueSelect compatible format
                this.fieldDefinitions[remoteFieldDefinition.type].options.push(toOption(remoteFieldDefinition));
            });

            this.isLoading = false;
        } catch (e) {
            errorGrowl(e);
        }
        
        //In case of a switch between regular dropdown -> multi dropdown, attempt to convert our values
        if(this.value) {
            if(this.isMultiDropDown && !Array.isArray(this.value)) {
                this.$emit('update:value', [this.value]);
            } else if (!this.isMultiDropDown && Array.isArray(this.value)) {
                //A value of undefined is fine, as long as it's not an array anymore
                this.$emit('update:value', this.value[0]);
            }
        }
        
        this.$emit('ready');
    },
    
    methods: {
        displayErrors() {
            this.$v.$touch();
        }
    }
}
</script>

<style scoped>
.condition-row {
    margin: 0.6rem 0;
}
</style>